/**
 * The pack stage's paging arithmetic, shared by every house (plan 2026_09_23_pack_stage_coda.md
 * § The shared pieces). Each house keeps its own rule for WHICH frames the card reaches
 * (`pagerLookIndices` in its `packs/pagerLooks.ts`); how the card steps through them lives here.
 *
 * The coda is the frame after the last look: the set ends with the visitor's own. It is one more
 * index, `sampleCount` (9 on a nine-sample pack), so `pack_look_advanced.look_index` reports it
 * with no new event shape, and `nextLookIndex` wraps through it like any other frame.
 */

/**
 * Step one frame, wrapping. Returns the index to land on, or null when there is nowhere to go.
 *
 * `from` can sit outside the reachable set — a streaming slot auto-follows into the big area before
 * it is done — so a gesture then steps in from the edge it came from rather than refusing to move.
 */
export function nextLookIndex(indices: readonly number[], from: number, delta: 1 | -1): number | null {
    if (indices.length < 2) return null
    const at = indices.indexOf(from)
    if (at < 0) return delta === 1 ? indices[0] : indices[indices.length - 1]
    return indices[(at + delta + indices.length) % indices.length]
}

/** The coda's index: one past the last sample. */
export function codaIndex(sampleCount: number): number {
    return Math.max(0, sampleCount)
}

export function isCoda(idx: number, sampleCount: number): boolean {
    return idx === codaIndex(sampleCount)
}

/** A house's reachable looks with the coda appended — last look → coda → first look. */
export function withCoda(indices: readonly number[], coda: number): number[] {
    return [...indices, coda]
}
